import { useMemo, useCallback } from 'react';
import { ColumnDefinitionEnumType } from '@mark43/cad-api';
import { UnitQueueRowData } from '../socket/stream-types';
import { useUnitQueueViews } from './unit-queue-views-provider';
import { useApplicationType } from '../common/hooks/use-application-type';
import columnDefinitionEnum from '../queues/column-definition.enum';
import { useUnitAssignment } from '../unit-assignment/unit-assignment-context';
import { ASSIGNED_USER_UNIT } from '../fr-units-list/constants';
import { EMPTY_GROUP_SENTINEL_HEADER } from '../queues/store/queue-store';
import {
    UNIT_BEAT_COLUMN_DEFINITION,
    UNIT_DISPATCH_AREA_COLUMN_DEFINITION,
    UNIT_EMERGENCY_LABEL,
} from './constants';

export function useFrUnitIdsToShowAndUnitsGroupingData() {
    const { unitQueueViewsRef } = useUnitQueueViews();

    const { isCad } = useApplicationType();
    const unit = useUnitAssignment();
    const unitId = unit?.unitId;

    const assignedUnits = unitQueueViewsRef.current[unitId || -1]?.eventQueueView?.assignedUnits;

    const unitIdsToAlwaysShow = useMemo(() => {
        if (isCad) return [];
        const assignedUnitsIds = assignedUnits?.map((unit) => unit.id);
        if (assignedUnitsIds?.length) {
            return assignedUnitsIds;
        } else if (unitId) {
            return [unitId];
        } else {
            return [];
        }
    }, [isCad, assignedUnits, unitId]);

    const getGroupValueData = useCallback(
        (columnDefinition: ColumnDefinitionEnumType) => {
            const getGroupValue = (data: UnitQueueRowData): string | undefined => {
                if (unitIdsToAlwaysShow.includes(data.id)) {
                    return ASSIGNED_USER_UNIT;
                }
                if (data.hasInProgressEmergency) {
                    return UNIT_EMERGENCY_LABEL;
                }

                const flatValue =
                    columnDefinition === UNIT_DISPATCH_AREA_COLUMN_DEFINITION ||
                    columnDefinition === UNIT_BEAT_COLUMN_DEFINITION
                        ? columnDefinitionEnum[columnDefinition].getFlatValue(data)
                        : columnDefinitionEnum.ASSIGNED_STATUS.getFlatValue(data);

                return flatValue?.trim() ? flatValue : EMPTY_GROUP_SENTINEL_HEADER;
            };

            return getGroupValue;
        },
        [unitIdsToAlwaysShow]
    );

    return { unitIdsToAlwaysShow, getGroupValueData };
}
